import type { PaginatedResult } from "@/view-models/Common/Pagination";
import type {
  AktivitetsomraadeView,
  FLStrukturVersionEntityView,
  HovedkontoView,
  UnderkontoView,
  ParagrafView,
  HovedomraadeView,
  updateParagrafInfoCommand,
  updateHovedomraadeInfoCommand,
  updateAktivitetsomraadeInfoCommand,
  updateHovedkontoInfoCommand,
  UpdateUnderkontoInfoCommand,
  CreateParagrafCommand,
  CreateHovedomraadeCommand,
  CreateAktivitetsomraadeCommand,
  CreateHovedkontoCommand,
  CreateUnderkontoCommand,
  BevillingstypeView,
  InsertModeStateView,
  UnderkontotypeView,
  AfslutParagrafCommand,
  AfslutHovedomraadeCommand,
  AfslutAktivitetsomraadeCommand,
  AfslutHovedkontoCommand,
  AfslutUnderkontoCommand,
  DelregnskabUnderkontoView,
  UnderkontoDelregnskabView,
  FLStructureElementStatusView,
  SoftCloseHovedkontoCommand,
  SoftCloseUnderkontoCommand,
  ReopenHovedkontoCommand,
  ReopenUnderkontoCommand,
  VirksomhedRelationView,
} from "@/view-models/Finanslovsstruktur/FLStrukturVersionEntityView";
import type {
  AccountingStructureVersionEntityView,
  AfslutBogfoeringskredsCommand,
  AfslutDelregnskabCommand,
  AfslutEANCommand,
  AfslutMinisteransvarsomraadeCommand,
  AfslutRegnskabsfoerendeInstitutionCommand,
  AfslutRegnskabskontoCommand,
  AfslutStandardkontoCommand,
  AfslutVirksomhedCommand,
  BaseType,
  BogfoeringskredsSettingView,
  BogfoeringskredsView,
  CreateAccountingAccountCommand,
  CreateBogfoeringskredsCommand,
  CreateDelregnskabCommand,
  CreateEANNumberCommand,
  CreateMinisteransvarsomraadeCommand,
  CreateRegnskabsfoerendeInstitutionCommand,
  CreateStandardAccountCommand,
  CreateVirksomhedCommand,
  DelregnskabView,
  EANView,
  HovedartView,
  MinisteransvarsomraadeView,
  MoveElementCommand,
  RegnskabsfoerendeInstitutionView,
  RegnskabskontoView,
  DataDistributorVirksomhedView,
  ReopenDelregnskabCommand,
  SearchDefaultDelregnskabView,
  StandardkontoView,
  StatuspostView,
  UpdateAccountingAccount,
  UpdateAccountingInstitution,
  UpdateBookeepingCircuit,
  UpdateCompany,
  UpdateMinister,
  UpdatePartialAccount,
  UpdateStandardAccount,
  VirksomhedView,
  GetVirksomhedCVRDataResponse,
  GetDefaultDelregnskabToExistingUnderkontoResponse,
} from "@/view-models/Regnskabsstruktur/AccountingStructureVersionEntityView";
import type {
  CreateUnderaktivitetCommand,
  UnderaktivitetsView,
  UpdateUnderaktivitetCommand,
} from "@/view-models/Process/UnderaktivitetsView";
import axios, { type AxiosResponse } from "axios";
import type { PeriodeElement } from "@/view-models/Periods/PeriodeElement";
import type {
  MDMEntityGroupView,
  PeriodControlEntityVersionView,
} from "@/view-models/Common/MDMEntityView";
import type { ReferenceNumberView, ReferenceView } from "@/view-models/Common/ReferenceView";
import type {
  CreateProcesCommand,
  ProcessView,
  UpdateProcesCommand,
} from "@/view-models/Process/ProcessView";
import type { Finansaar } from "@/view-models/Finansaar/Finansaar";
import type {
  CreateHovedaktivitetCommand,
  HovedaktivitetsView,
  UpdateHovedaktivitetCommand,
} from "@/view-models/Process/HovedaktivitetsView";
import type {
  ProcessRettighederView,
  UpdateProcesrettighederCommand,
} from "@/view-models/Process/ParagrafProcessRettighedView";
import type { UserProfileView } from "@/view-models/Common/UserProfileView";
import type { UpdatePeriodeElementCommand } from "@/view-models/Periods/UpdatePeriodeElementCommand";
import type {
  CreatePeriodeElementCommand,
  CreatePeriodeElementsForCompanyCodesCommand,
} from "@/view-models/Periods/CreatePeriodeElementCommand";
import type { GeneratePeriodeElementsCommand } from "@/view-models/Periods/GeneratePeriodeElementsCommand";
import type { AktivitetView } from "@/view-models/Aktiviteter/AktivitetView";
import type { UpdateAktivitetInfoCommand } from "@/view-models/Aktiviteter/UpdateAktivitetInfoCommand";
import type { CreateAktivitetCommand } from "@/view-models/Aktiviteter/CreateAktivitetCommand";
import type { DeleteAktivitetCommand } from "@/view-models/Aktiviteter/DeleteAktivitetCommand";
import type { ApprovePeriodeElementsCommand } from "@/view-models/Periods/ApprovePeriodeElementsCommand";
import type { GetLoenfaellesskabDetailResponseView } from "@/view-models/SLP/GetLoenfaellesskabDetailResponseView";
import type { GetLoenfaellesskabVersionResponseItemView } from "@/view-models/SLP/GetLoenfaellesskabVersionResponseItemView";
import type { SLPEntityView } from "@/view-models/SLP/SLPEntityView";
import type { GetLongruppeVersionResponseItemView } from "@/view-models/SLP/GetLongruppeVersionResponseItemView";
import type { GetLongruppeDetailResponseView } from "@/view-models/SLP/GetLongruppeDetailResponseView";
import type { UpsertLoenfaellesskabResponseView } from "@/view-models/SLP/UpsertLoenfaellesskabResponseView";
import type { UpsertLoengruppeResponseView } from "@/view-models/SLP/UpsertLoengruppeResponseView";
import type { UpsertLoengruppeCommand } from "@/view-models/SLP/UpsertLoengruppeCommand";
import type { GetLoenfaellesskaberResponseItemView } from "@/view-models/SLP/GetLoenfaellesskaberResponseItemView";
import type { GetLoengrupperResponseItemView } from "@/view-models/SLP/GetLoengrupperResponseItemView";
import type { UpsertLoenfaellesskabCommand } from "@/view-models/SLP/UpsertLoenfaellesskabCommand";
import {
  KontobroerType,
  type GetKontobroQueryView,
  type UpsertKontoFraCorrectionsCommand,
} from "@/view-models/kontobroer/KontobroerView";
import type {
  SksFLHovedkontoReferenceView,
  SksFLSearchRequest,
  SksFLSearchResponseView,
} from "@/view-models/Report/ReportFLView";
import type {
  SksRegDetailHeaderView,
  SksRegOutputLevel,
  SksRegSearchRequest,
  SksRegSearchResponseView,
} from "@/view-models/Report/ReportRegView";

const responseBody = <T>(response: AxiosResponse<T>) => {
  return response.data;
};

const requests = {
  getWithParams: <T>(url: string, requestParams: any) =>
    axios.get<T>(url, requestParams).then(responseBody),
  get: <T>(url: string) => axios.get<T>(url).then(responseBody),
  post: <T>(url: string, body: {}) => axios.post<T>(url, body).then(responseBody),
  put: <T>(url: string, body: {}) => axios.put<T>(url, body).then(responseBody),
  del: <T>(url: string, body: {}) => axios.delete<T>(url, { data: body }).then(responseBody),
};

const SLP = {
  search: (requestParams: any) => {
    return requests.getWithParams<PaginatedResult<SLPEntityView>>(`/api/slp/search`, requestParams);
  },
  getLoenfaellesskaber: (): Promise<GetLoenfaellesskaberResponseItemView[]> => {
    return requests.get<GetLoenfaellesskaberResponseItemView[]>("api/slp/loenfaellesskaber");
  },
  getLoenfaellesskabVersions: (
    nummer: number
  ): Promise<GetLoenfaellesskabVersionResponseItemView[]> => {
    const urlSearchParams = new URLSearchParams();
    urlSearchParams.set("nummer", nummer.toString());
    const requestParams = {
      params: urlSearchParams,
    };
    return requests.getWithParams<GetLoenfaellesskabVersionResponseItemView[]>(
      "api/slp/loenfaellesskab/versions",
      requestParams
    );
  },
  getLoenfaellesskabDetail: (
    nummer: number,
    versionId?: number
  ): Promise<GetLoenfaellesskabDetailResponseView> => {
    const urlSearchParams = new URLSearchParams();
    urlSearchParams.set("nummer", nummer.toString());
    if (versionId) {
      urlSearchParams.set("versionId", versionId.toString());
    }
    const requestParams = {
      params: urlSearchParams,
    };
    return requests.getWithParams<GetLoenfaellesskabDetailResponseView>(
      "api/slp/loenfaellesskab",
      requestParams
    );
  },
  createLoenfaellesskab: (
    payload: UpsertLoenfaellesskabCommand
  ): Promise<UpsertLoenfaellesskabResponseView> => {
    return requests.post<UpsertLoenfaellesskabResponseView>("api/slp/loenfaellesskab", payload);
  },
  updateLoenfaellesskab: (
    payload: UpsertLoenfaellesskabCommand
  ): Promise<UpsertLoenfaellesskabResponseView> => {
    return requests.put<UpsertLoenfaellesskabResponseView>("api/slp/loenfaellesskab", payload);
  },
  checkLongruppeExists: (nummer: number): Promise<boolean> => {
    const urlSearchParams = new URLSearchParams();
    urlSearchParams.set("nummer", nummer.toString());
    const requestParams = {
      params: urlSearchParams,
    };
    return requests.getWithParams<boolean>(`/api/slp/loengruppe/exists`, requestParams);
  },
  getLongruppeDetail: (
    nummer: number,
    versionId?: number
  ): Promise<GetLongruppeDetailResponseView> => {
    const urlSearchParams = new URLSearchParams();
    urlSearchParams.set("nummer", nummer.toString());
    if (versionId) {
      urlSearchParams.set("versionId", versionId.toString());
    }
    const requestParams = {
      params: urlSearchParams,
    };
    return requests.getWithParams<GetLongruppeDetailResponseView>(
      `/api/slp/loengruppe`,
      requestParams
    );
  },
  getLoengrupper: (): Promise<GetLoengrupperResponseItemView[]> => {
    return requests.get<GetLoengrupperResponseItemView[]>("api/slp/loengrupper");
  },
  getLongruppeVersions: (nummer: number): Promise<GetLongruppeVersionResponseItemView[]> => {
    const urlSearchParams = new URLSearchParams();
    urlSearchParams.set("nummer", nummer.toString());
    const requestParams = {
      params: urlSearchParams,
    };
    return requests.getWithParams<GetLongruppeVersionResponseItemView[]>(
      `/api/slp/loengruppe/versions`,
      requestParams
    );
  },
  createLoengruppe: (payload: UpsertLoengruppeCommand): Promise<UpsertLoengruppeResponseView> => {
    return requests.post<UpsertLoengruppeResponseView>("api/slp/loengruppe", payload);
  },
  updateLoengruppe: (payload: UpsertLoengruppeCommand): Promise<UpsertLoengruppeResponseView> => {
    return requests.put<UpsertLoengruppeResponseView>("api/slp/loengruppe", payload);
  },
  slpDownloadExcel: (requestParams: any) =>
    axios({
      url: "api/slp/download-excel",
      method: "GET",
      params: requestParams,
      responseType: "blob", // important
    }).then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "loenfaellesskabs.xlsx");
      document.body.appendChild(link);
      link.click();
    }),
};

const PeriodElements = {
  getAllPeriods: () => requests.get<PeriodeElement[]>("api/perioder"),
  GetAllActivePerioder: () => requests.get<PeriodeElement[]>("api/perioder/active"),
  GetCurrentPeriod: () => requests.get<PeriodeElement>("api/perioder/current"),
  getPeriodByNumber: (periodNumber: number) => requests.get(`api/perioder/${periodNumber}`),
  getPeriodById: (periodId: number) => requests.get(`api/perioder/by-id/${periodId}`),
  updatePeriod: (body: UpdatePeriodeElementCommand) => requests.put("api/perioder", body),
  createPeriod: (body: CreatePeriodeElementCommand) => requests.post("api/perioder", body),
  createPeriodeElementsForMultipleCompanyCodes: (
    body: CreatePeriodeElementsForCompanyCodesCommand
  ) => requests.post("api/perioder/for-multiple-company-codes", body),
  createPeriods: (body: GeneratePeriodeElementsCommand) =>
    requests.post("api/perioder/generate-periods", body),
  approvePeriodeElements: (body: ApprovePeriodeElementsCommand) =>
    requests.post<any[]>("api/perioder/approve-periods", body),
};

const Aktiviteter = {
  getAktiviteter: () => requests.get<AktivitetView[]>("api/aktiviteter"),
  updateAktivitet: (body: UpdateAktivitetInfoCommand) => requests.put("api/aktiviteter", body),
  createAktivitet: (body: CreateAktivitetCommand) => requests.post("api/aktiviteter", body),
  deleteAktivitet: (body: DeleteAktivitetCommand) => requests.del("api/aktiviteter", body),
};

const FLStructureElements = {
  searchFLStructureElements: (requestParams: any) =>
    requests.getWithParams<PaginatedResult<FLStrukturVersionEntityView>>(
      `/api/finanslovsstruktur/paged-search`,
      requestParams
    ),
  searchFullFLStructureElements: (requestParams: any) =>
    requests.getWithParams<FLStrukturVersionEntityView[]>(
      `/api/finanslovsstruktur/search`,
      requestParams
    ),
  searchFullNumberFLStructureElements: (requestParams: any) =>
    requests.getWithParams<FLStrukturVersionEntityView[]>(
      `/api/finanslovsstruktur/search-full-number`,
      requestParams
    ),
  getFLStructureElementStatus: (requestParams: any) =>
    requests.getWithParams<FLStructureElementStatusView>(
      `/api/finanslovsstruktur/status`,
      requestParams
    ),
  getParagraphByNumber: (requestParams: any, number: string) =>
    requests.getWithParams<ParagrafView>(
      `/api/finanslovsstruktur/paragraffer/byNummer` + "/" + number,
      requestParams
    ),
  getParagrafferByCampaign: (underaktivitetId: number) => {
    const requestParams = {
      params: new URLSearchParams({
        underaktivitetId: underaktivitetId.toString(),
      }),
    };
    return requests.getWithParams<ParagrafView[]>(
      `/api/finanslovsstruktur/paragraffer`,
      requestParams
    );
  },

  getMainAreaByNumber: (requestParams: any, number: string) =>
    requests.getWithParams<HovedomraadeView>(
      `/api/finanslovsstruktur/hovedomraader/byNummer` + "/" + number,
      requestParams
    ),

  getHovedomraaderByCampaign: (underaktivitetId: number) => {
    const requestParams = {
      params: new URLSearchParams({
        underaktivitetId: underaktivitetId.toString(),
      }),
    };
    return requests.getWithParams<HovedomraadeView[]>(
      `/api/finanslovsstruktur/hovedomraader`,
      requestParams
    );
  },

  getActivityAreaByNumber: (requestParams: any, number: string) =>
    requests.getWithParams<AktivitetsomraadeView>(
      `/api/finanslovsstruktur/aktivitetsomraader/byNummer` + "/" + number,
      requestParams
    ),

  getAktivitetsomraaderByCampaign: (underaktivitetId: number) => {
    const requestParams = {
      params: new URLSearchParams({
        underaktivitetId: underaktivitetId.toString(),
      }),
    };
    return requests.getWithParams<AktivitetsomraadeView[]>(
      `/api/finanslovsstruktur/aktivitetsomraader`,
      requestParams
    );
  },
  getMainAccountByNumber: (requestParams: any, number: string) =>
    requests.getWithParams<HovedkontoView>(
      `/api/finanslovsstruktur/hovedkonti/byNummer` + "/" + number,
      requestParams
    ),

  getHovedkontiByCampaign: (underaktivitetId: number) => {
    const requestParams = {
      params: new URLSearchParams({
        underaktivitetId: underaktivitetId.toString(),
      }),
    };
    return requests.getWithParams<HovedkontoView[]>(
      `/api/finanslovsstruktur/hovedkonti`,
      requestParams
    );
  },

  getHovedkontisAsReferencesByCampaign: (underaktivitetId: number) => {
    const requestParams = {
      params: new URLSearchParams({
        underaktivitetId: underaktivitetId.toString(),
      }),
    };
    return requests.getWithParams<ReferenceView[]>(
      `/api/finanslovsstruktur/hovedkonti/references`,
      requestParams
    );
  },

  getUnderkontiAsReferencesByYear: (year: number) => {
    return requests.get<ReferenceView[]>(
      `/api/finanslovsstruktur/underkonti/byYear/${year}/references`
    );
  },

  getSubAccountByNumber: (requestParams: any, number: string) =>
    requests.getWithParams<UnderkontoView>(
      `/api/finanslovsstruktur/underkonti/byNummer` + "/" + number,
      requestParams
    ),
  getMissingLinkSubAccounts: () =>
    axios({
      url: "api/finanslovsstruktur/underkonti/missing-delregnskab/excel",
      method: "GET",
      responseType: "blob", // important
    }).then((response) => {
      // Make virtual a tag for trigger download file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        "Rapport over manglende kobling mellem underkonto og delregnskab.xlsx"
      );
      document.body.appendChild(link);
      link.click();
    }),
  getUnderkontotypesByCampaign: (requestParams: any) =>
    requests.getWithParams<UnderkontotypeView[]>(
      `/api/finanslovsstruktur/underkontotypes/`,
      requestParams
    ),
  getVirksomhedsbaerendeHovedkontiByCampaign: (requestParams: any) =>
    requests.getWithParams<ReferenceView[]>(
      `/api/finanslovsstruktur/virksomhedsbaerende-hovedkonti/`,
      requestParams
    ),
  getVersionsByIdAndType: (requestParams: any) =>
    requests.getWithParams<MDMEntityGroupView[]>(
      `/api/finanslovsstruktur/versioner`,
      requestParams
    ),
  flStructureDownload: (requestParams: any) =>
    axios({
      url: "api/finanslovsstruktur/search-download-excel",
      method: "GET",
      params: requestParams,
      responseType: "blob", // important
    }).then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "finanslovsstruktur.xlsx");
      document.body.appendChild(link);
      link.click();
    }),
  getFLLatestVersionsByType: (requestParams: any) =>
    requests.getWithParams<ReferenceView[]>(
      `/api/finanslovsstruktur/latest-versions`,
      requestParams
    ),
  getAllBevillingstyper: () =>
    requests.get<BevillingstypeView[]>(`/api/finanslovsstruktur/bevillingstyper`),
  getFlStrukturElementsChildren: (requestParams: any, number: string) =>
    requests.getWithParams<FLStrukturVersionEntityView[]>(
      `/api/finanslovsstruktur/` + number + "/child-elements",
      requestParams
    ),
  getAcStrukturElementsChildren: (requestParams: any, number: string) =>
    requests.getWithParams<PeriodControlEntityVersionView[]>(
      `/api/regnskabsstruktur/` + number + "/child-elements",
      requestParams
    ),
  getInsertModeStateByNumberSubActivityAndType: (requestParams: any) =>
    requests.getWithParams<InsertModeStateView>(
      `/api/finanslovsstruktur/insert-mode-state`,
      requestParams
    ),
};

const PutFLStructure = {
  UpdateParagrafInfo: (body: updateParagrafInfoCommand) =>
    requests.put(`/api/finanslovsstruktur/paragraffer?id=${body.paragrafId}`, body),
  UpdateHovedomraadeInfo: (body: updateHovedomraadeInfoCommand) =>
    requests.put(`/api/finanslovsstruktur/hovedomraader?id=${body.hovedomraadeId}`, body),
  UpdateAktivitetsomraadeInfo: (body: updateAktivitetsomraadeInfoCommand) =>
    requests.put(`/api/finanslovsstruktur/aktivitetsomraader?id=${body.aktivitetsomraadeId}`, body),
  UpdateHovedkontoInfo: (body: updateHovedkontoInfoCommand) =>
    requests.put(`/api/finanslovsstruktur/hovedkonti?id=${body.hovedkontoId}`, body),
  UpdateUnderkontotypeInfo: (body: UpdateUnderkontoInfoCommand) =>
    requests.put(`/api/finanslovsstruktur/underkonti?id=${body.underkontoId}`, body),
};

const ACAndFLRelation = {
  searchDefaultDelregnskab: (requestParams: any) =>
    requests.getWithParams<SearchDefaultDelregnskabView>(
      `/api/regnskabsstruktur/delregnskaber/search-default-delregnskab`,
      requestParams
    ),
  getUnderkontiRelations: (delregnskabId: number) =>
    requests.get<DelregnskabUnderkontoView[]>(
      `/api/regnskabsstruktur/delregnskaber/` + delregnskabId + "/all-underkonti-relationer"
    ),
  getDelregnskaberRelations: (underkontoId: number, requestParams: any) =>
    requests.getWithParams<UnderkontoDelregnskabView[]>(
      `/api/finanslovsstruktur/underkonti/${underkontoId}/delregnskaber-relationer`,
      requestParams
    ),
  getVirksomhederRelations: (requestParams: any, number: string) =>
    requests.getWithParams<VirksomhedRelationView[]>(
      `/api/finanslovsstruktur/` + number + "/virksomheder-relationer",
      requestParams
    ),
  putUnderkontiRelations: (
    delregnskabId: number,
    underkontoId: number,
    defaultDelregnskab: boolean,
    periodFrom: string
  ) =>
    requests.put(`/api/regnskabsstruktur/underkonto-delregnskab/`, {
      delregnskabId,
      underkontoId,
      defaultDelregnskab,
      fraPeriode: periodFrom,
    }),
  getOtherDefaultDelregnskabToExistingUnderkonto: (
    delregnskabId: number,
    underkontoId: number,
    periodFrom: string
  ) =>
    requests.get<GetDefaultDelregnskabToExistingUnderkontoResponse>(
      `/api/regnskabsstruktur/delregnskaber/${delregnskabId}/other-default-delregnskabs/${underkontoId}/${periodFrom}`
    ),
};

const AccountingStructureElements = {
  pagedSearchAccountingStructureElements: (requestParams: any) =>
    requests.getWithParams<PaginatedResult<AccountingStructureVersionEntityView>>(
      `/api/regnskabsstruktur/paged-search`,
      requestParams
    ),
  searchFullNumberACStructureElements: (requestParams: any) =>
    requests.getWithParams<AccountingStructureVersionEntityView[]>(
      `/api/regnskabsstruktur/search-full-number`,
      requestParams
    ),
  searchAccountingStructureElements: (requestParams: any) =>
    requests.getWithParams<AccountingStructureVersionEntityView[]>(
      `/api/regnskabsstruktur/search`,
      requestParams
    ),
  getMinisterByNumber: (requestParams: any, number: string) =>
    requests.getWithParams<MinisteransvarsomraadeView>(
      `/api/regnskabsstruktur/ministeransvarsomraader/byNummer` + "/" + number,
      requestParams
    ),
  getMinisteransvarsomraaderByPeriod: (periode: number) => {
    const requestParams = {
      params: new URLSearchParams({ periode: periode.toString() }),
    };
    return requests.getWithParams<MinisteransvarsomraadeView[]>(
      `/api/regnskabsstruktur/ministeransvarsomraader`,
      requestParams
    );
  },
  getCompanyByNumber: (requestParams: any, number: string) =>
    requests.getWithParams<VirksomhedView>(
      `/api/regnskabsstruktur/virksomheder/byNummer` + "/" + number,
      requestParams
    ),
  getCompanyById: (requestParams: any, id: number) =>
    requests.getWithParams<VirksomhedView>(
      `/api/regnskabsstruktur/virksomheder` + "/" + id,
      requestParams
    ),
  getVirksomhederByPeriod: (periode: number) => {
    const requestParams = {
      params: new URLSearchParams({ periode: periode.toString() }),
    };
    return requests.getWithParams<VirksomhedView[]>(
      `/api/regnskabsstruktur/virksomheder`,
      requestParams
    );
  },
  getLatestVirksomheder: () => {
    const requestParams = {};
    return requests.getWithParams<VirksomhedView[]>(
      `/api/regnskabsstruktur/virksomheder/latest`,
      requestParams
    );
  },
  getVirksomhederCVRData: (cvrNumber: string) => {
    return requests.get<GetVirksomhedCVRDataResponse | undefined>(
      `/api/regnskabsstruktur/virksomheder/${cvrNumber}/cvr-data`
    );
  },
  getCompanyTypes: (requestParams: any) =>
    requests.getWithParams<BaseType[]>(`/api/regnskabsstruktur/virksomhedstyper`, requestParams),
  getAccountingCircleTypes: (requestParams: any) =>
    requests.getWithParams<BaseType[]>(`/api/regnskabsstruktur/bogfoeringstyper`, requestParams),
  getAccountingInstitutionByNumber: (requestParams: any, number: string) =>
    requests.getWithParams<RegnskabsfoerendeInstitutionView>(
      `/api/regnskabsstruktur/regnskabsfoerendeinstitutioner/byNummer` + "/" + number,
      requestParams
    ),
  getRegnskabsfoerendeInstitutionerByPeriod: (periode: number) => {
    const requestParams = {
      params: new URLSearchParams({ periode: periode.toString() }),
    };
    return requests.getWithParams<RegnskabsfoerendeInstitutionView[]>(
      `/api/regnskabsstruktur/regnskabsfoerendeinstitutioner`,
      requestParams
    );
  },
  getBookkeepingCircuitByNumber: (requestParams: any, number: string) =>
    requests.getWithParams<BogfoeringskredsView>(
      `/api/regnskabsstruktur/bogfoeringskredse/byNummer` + "/" + number,
      requestParams
    ),
  getBookkeepingCircuitByBogfoeringskredsnummer: (requestParams: any, number: string) =>
    requests.getWithParams<BogfoeringskredsView>(
      `/api/regnskabsstruktur/bogfoeringskredse/byBogfoeringskredsnummer` + "/" + number,
      requestParams
    ),
  getBogfoeringskredseByPeriod: (periode: number) => {
    const requestParams = {
      params: new URLSearchParams({ periode: periode.toString() }),
    };
    return requests.getWithParams<BogfoeringskredsView[]>(
      `/api/regnskabsstruktur/bogfoeringskredse`,
      requestParams
    );
  },
  getBogfoeringskredseSettingByCanonicalNumber: (canonicalNumber: string) => {
    return requests.get<BogfoeringskredsSettingView>(
      `/api/regnskabsstruktur/bogfoeringskredse/settings/${canonicalNumber}`
    );
  },
  getPartialAccountByNumber: (requestParams: any, number: string) =>
    requests.getWithParams<DelregnskabView>(
      `/api/regnskabsstruktur/delregnskaber/byNummer` + "/" + number,
      requestParams
    ),
  getDelregnskaberByPeriod: (period: number) => {
    const requestParams = {
      params: new URLSearchParams({ periode: period.toString() }),
    };
    return requests.getWithParams<DelregnskabView[]>(
      `/api/regnskabsstruktur/delregnskaber`,
      requestParams
    );
  },
  GetEANByNumber: (requestParams: any, number: string) =>
    requests.getWithParams<EANView>(
      `/api/regnskabsstruktur/EANNumre/byNummer` + "/" + number,
      requestParams
    ),
  getStandardkontoByNumber: (requestParams: any, number: string) =>
    requests.getWithParams<StandardkontoView>(
      `/api/kontoplan/standardkonti/byNummer` + "/" + number,
      requestParams
    ),

  getStandardkontiAsReferencesByPeriod: (periode: number) => {
    return requests.get<ReferenceView[]>(
      `/api/kontoplan/standardkonti/byPeriod/${periode}/references`
    );
  },

  getAllStandardkonti: () => requests.get<ReferenceNumberView[]>(`/api/kontoplan/standardkonti`),
  getRegnskabskontoViewByNumber: (requestParams: any, number: string) =>
    requests.getWithParams<RegnskabskontoView>(
      `/api/kontoplan/regnskabskonti/byNummer` + "/" + number,
      requestParams
    ),

  getRegnskabskontiAsReferencesByPeriod: (periode: number) => {
    return requests.get<ReferenceView[]>(
      `/api/kontoplan/regnskabskonti/byPeriod/${periode}/references`
    );
  },

  getStatusposterByPeriode: (periode: number) => {
    const requestParams = {
      params: new URLSearchParams({ periode: periode.toString() }),
    };
    return requests.getWithParams<StatuspostView[]>(`/api/kontoplan/statusposter`, requestParams);
  },

  getKontoplan: (periode: number) => {
    const requestParams = {
      params: new URLSearchParams({ period: periode.toString() }),
    };
    return requests.getWithParams<StandardkontoView[]>(`/api/kontoplan`, requestParams);
  },
  getAllHovedarts: () => requests.get<HovedartView[]>("/api/kontoplan/hovedarter"),
  getVersionsByNumberAndType: (requestParams: any) =>
    requests.getWithParams<PeriodControlEntityVersionView[]>(
      `/api/regnskabsstruktur/versioner`,
      requestParams
    ),
  getACLatestVersionsByType: (requestParams: any) =>
    requests.getWithParams<ReferenceView[]>(
      `/api/regnskabsstruktur/latest-versions`,
      requestParams
    ),
  accountingStructureDownload: (requestParams: any) =>
    axios({
      url: "api/regnskabsstruktur/search-download-excel",
      method: "GET",
      params: requestParams,
      responseType: "blob", // important
    }).then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "regnskabsstruktur.xlsx");
      document.body.appendChild(link);
      link.click();
    }),
};

const Datafordeler = {
  getDataDistributorVirksomhed: (cvrNummer: string) => {
    const requestParams = {
      params: new URLSearchParams({ cvrNummer: cvrNummer }),
    };
    return requests.getWithParams<DataDistributorVirksomhedView>(
      `/api/datafordeler/cvr-data`,
      requestParams
    );
  },
  getSENumreByCVR: (cvrNummer: string) => {
    const requestParams = {
      params: new URLSearchParams({ cvrNummer: cvrNummer }),
    };
    return requests.getWithParams<string[]>(`/api/datafordeler/senumre`, requestParams);
  },
};

const PutACElement = {
  UpdateMinisterArea: (body: UpdateMinister) =>
    requests.put(`/api/regnskabsstruktur/ministeransvarsomraader`, body),
  UpdateCompany: (body: UpdateCompany) => requests.put(`/api/regnskabsstruktur/virksomheder`, body),
  UpdateAccountingInstitution: (body: UpdateAccountingInstitution) =>
    requests.put(`/api/regnskabsstruktur/regnskabsfoerendeinstitutioner`, body),
  UpdateBookeepingCircuit: (body: UpdateBookeepingCircuit) =>
    requests.put(`/api/regnskabsstruktur/bogfoeringskredse`, body),
  UpdatePartialAccount: (body: UpdatePartialAccount) =>
    requests.put(`/api/regnskabsstruktur/delregnskaber`, body),
  ReopenPartialAccount: (body: ReopenDelregnskabCommand) =>
    requests.put(`/api/regnskabsstruktur/delregnskaber/reopen`, body),
  UpdateStandardAccount: (body: UpdateStandardAccount) =>
    requests.put(`/api/kontoplan/standardkonti`, body),
  UpdateAccountingAccount: (body: UpdateAccountingAccount) =>
    requests.put(`/api/kontoplan/regnskabskonti`, body),
  UpdateEANNumber: (body: CreateEANNumberCommand) =>
    requests.put(`/api/regnskabsstruktur/EANNumre`, body),
};

const CreateACElement = {
  MoveElement: (body: MoveElementCommand) =>
    requests.post(`/api/regnskabsstruktur/move-element`, body),
  createMinisteransvarsomraade: (body: CreateMinisteransvarsomraadeCommand) =>
    requests.post(`/api/regnskabsstruktur/ministeransvarsomraader`, body),
  createVirksomhed: (body: CreateVirksomhedCommand) =>
    requests.post(`/api/regnskabsstruktur/virksomheder`, body),
  createRegnskabsfoerendeInstitution: (body: CreateRegnskabsfoerendeInstitutionCommand) =>
    requests.post(`/api/regnskabsstruktur/regnskabsfoerendeinstitutioner`, body),
  createBogfoeringskreds: (body: CreateBogfoeringskredsCommand) =>
    requests.post(`/api/regnskabsstruktur/bogfoeringskredse`, body),
  createDelregnskab: (body: CreateDelregnskabCommand) =>
    requests.post(`/api/regnskabsstruktur/delregnskaber`, body),
  createEANNumber: (body: CreateEANNumberCommand) =>
    requests.post(`/api/regnskabsstruktur/EANNumre`, body),
  createStandardAccount: (body: CreateStandardAccountCommand) =>
    requests.post(`/api/kontoplan/standardkonti`, body),
  createAccountingAccount: (body: CreateAccountingAccountCommand) =>
    requests.post(`/api/kontoplan/regnskabskonti`, body),
};

const CreateFLElement = {
  createParagraf: (body: CreateParagrafCommand) =>
    requests.post(`/api/finanslovsstruktur/paragraffer`, body),
  createHovedomraade: (body: CreateHovedomraadeCommand) =>
    requests.post(`/api/finanslovsstruktur/hovedomraader`, body),
  createAktivitetsomraade: (body: CreateAktivitetsomraadeCommand) =>
    requests.post(`/api/finanslovsstruktur/aktivitetsomraader`, body),
  createHovedkonto: (body: CreateHovedkontoCommand) =>
    requests.post(`/api/finanslovsstruktur/hovedkonti`, body),
  createUnderkonto: (body: CreateUnderkontoCommand) =>
    requests.post(`/api/finanslovsstruktur/underkonti`, body),
};

const DeleteFLElement = {
  deleteParagraf: (id: number, body: AfslutParagrafCommand) =>
    requests.del(`/api/finanslovsstruktur/paragraffer?id=${id}`, body),
  deleteHovedomraade: (id: number, body: AfslutHovedomraadeCommand) =>
    requests.del(`/api/finanslovsstruktur/hovedomraader?id=${id}`, body),
  deleteAktivitetsomraade: (id: number, body: AfslutAktivitetsomraadeCommand) =>
    requests.del(`/api/finanslovsstruktur/aktivitetsomraader?id=${id}`, body),
  deleteHovedkonto: (id: number, body: AfslutHovedkontoCommand) =>
    requests.del(`/api/finanslovsstruktur/hovedkonti?id=${id}`, body),
  deleteUnderkonto: (id: number, body: AfslutUnderkontoCommand) =>
    requests.del(`/api/finanslovsstruktur/underkonti?id=${id}`, body),
};
const SoftCloseFLElement = {
  softCloseHovedkonto: (id: number, body: SoftCloseHovedkontoCommand) =>
    requests.put(`/api/finanslovsstruktur/hovedkonti/soft-close?id=${id}`, body),
  softCloseUnderkonto: (id: number, body: SoftCloseUnderkontoCommand) =>
    requests.put(`/api/finanslovsstruktur/underkonti/soft-close?id=${id}`, body),
  reopenHovedkonto: (id: number, body: ReopenHovedkontoCommand) =>
    requests.put(`/api/finanslovsstruktur/hovedkonti/reopen?id=${id}`, body),
  reopenUnderkonto: (id: number, body: ReopenUnderkontoCommand) =>
    requests.put(`/api/finanslovsstruktur/underkonti/reopen?id=${id}`, body),
};

const DeleteACElement = {
  afslutMinisteransvarsomraade: (body: AfslutMinisteransvarsomraadeCommand) =>
    requests.del(`/api/regnskabsstruktur/ministeransvarsomraader`, body),
  afslutVirksomhed: (body: AfslutVirksomhedCommand) =>
    requests.del(`/api/regnskabsstruktur/virksomheder`, body),
  afslutRegnskabsfoerendeInstitution: (body: AfslutRegnskabsfoerendeInstitutionCommand) =>
    requests.del(`/api/regnskabsstruktur/regnskabsfoerendeinstitutioner`, body),
  afslutBogfoeringskreds: (body: AfslutBogfoeringskredsCommand) =>
    requests.del(`/api/regnskabsstruktur/bogfoeringskredse`, body),
  afslutDelregnskab: (body: AfslutDelregnskabCommand) =>
    requests.del(`/api/regnskabsstruktur/delregnskaber`, body),
  afslutStandardkonto: (body: AfslutStandardkontoCommand) =>
    requests.del(`/api/kontoplan/standardkonti`, body),
  afslutRegnskabskonto: (body: AfslutRegnskabskontoCommand) =>
    requests.del(`/api/kontoplan/regnskabskonti`, body),
  afslutEAN: (body: AfslutEANCommand) => requests.del(`/api/regnskabsstruktur/EANNumre`, body),
};

const Processes = {
  getProcessrettigheder: (requestParams: any) =>
    requests.getWithParams<ProcessRettighederView>(`/api/proces/processrettigheder`, requestParams),
  getProcessrettighederByProcesId: (procesId: number) =>
    requests.get<ProcessRettighederView>(`/api/proces/processrettigheder/${procesId}`),
  getAllSubActivities: (requestParams: any | undefined = undefined) =>
    requests.getWithParams<UnderaktivitetsView[]>(`/api/proces/underaktiviteter`, requestParams),
  getAllActiveProcesses: (requestParams: any) =>
    requests.getWithParams<ProcessView[]>(`/api/proces/active`, requestParams),
  getProcessById: (procesId: number) =>
    requests.get<ProcessView>(`/api/proces/processer/${procesId}`),
  getMainActivityById: (hovedaktivitetId: number) =>
    requests.get<HovedaktivitetsView>(`/api/proces/hovedaktiviteter/${hovedaktivitetId}`),
  getSubActivityById: (underaktivitetId: number) =>
    requests.get<UnderaktivitetsView>(`/api/proces/underaktiviteter/${underaktivitetId}`),
  createProces: (body: CreateProcesCommand) => requests.post(`/api/proces/processer`, body),
  updateProces: (body: UpdateProcesCommand) => requests.put(`/api/proces/processer`, body),
  createHovedaktivitet: (body: CreateHovedaktivitetCommand) =>
    requests.post(`/api/proces/hovedaktiviteter`, body),
  updateHovedaktivitet: (body: UpdateHovedaktivitetCommand) =>
    requests.put(`/api/proces/hovedaktiviteter`, body),
  createUnderaktivitet: (body: CreateUnderaktivitetCommand) =>
    requests.post(`/api/proces/underaktiviteter`, body),
  updateUnderaktivitet: (body: UpdateUnderaktivitetCommand) =>
    requests.put(`/api/proces/underaktiviteter`, body),

  updatUpdateProcesrettighederInterneProces: (body: UpdateProcesrettighederCommand) =>
    requests.put(`/api/proces/procesrettigheder-intern`, body),
};

const FinancialYears = {
  GetAllFinansaar: () => requests.get<Finansaar[]>(`/api/finansaar`),
};

const UserRoles = {
  GetUserProfile: () => requests.get<UserProfileView>(`/api/administration/user-profile`),
};
const Kontobroer = {
  getSelectionYears: () => requests.get<number[]>(`/api/kontobroer/years`),
  getKontobroer: (year: string, kontobroType: KontobroerType, includeInactive?: boolean | null) => {
    const params = new URLSearchParams({
      year: year,
      kontobroType: kontobroType,
    });
    if (includeInactive !== null && includeInactive !== undefined) {
      params.append("includeInactive", `${includeInactive}`);
    }
    const requestParams = {
      params: params,
    };
    return requests.getWithParams<GetKontobroQueryView>(`/api/kontobroer`, requestParams);
  },
  getCalculatedKontobro: (year: string, kontoFra: string) => {
    const requestParams = {
      params: new URLSearchParams({
        year: year,
        kontobroType: KontobroerType.KontobroerBeregnet,
        kontoFra: kontoFra,
      }),
    };
    return requests.getWithParams<GetKontobroQueryView>(`/api/kontobroer`, requestParams);
  },
  getCorrectedKontobro: (year: string, kontoFra: string) => {
    const requestParams = {
      params: new URLSearchParams({
        year: year,
        kontobroType: KontobroerType.KorrektionerTilVertikaleKontobroer,
        kontoFra: kontoFra,
        includeInactive: "true",
      }),
    };
    return requests.getWithParams<GetKontobroQueryView>(`/api/kontobroer`, requestParams);
  },
  upsertCorrection: (command: UpsertKontoFraCorrectionsCommand) => {
    return requests.put(`/api/kontobroer/corrections`, command);
  },
  exportExcel: (year: string, kontobroType: KontobroerType) =>
    axios({
      url: "api/kontobroer/export",
      method: "GET",
      params: { year: year, kontobroType: kontobroType },
      responseType: "blob",
    }).then((response) => {
      // Make virtual a tag for trigger download file
      const filename = `${kontobroType}_${parseInt(year) - 1}_${year}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
    }),
};

const Report = {
  FL: {
    getYears: () => requests.get<number[]>("api/sks/fl/years"),
    getKampagneNames: (year?: number) => {
      const urlSearchParams = new URLSearchParams();
      if (year !== undefined) {
        urlSearchParams.set("year", year.toString());
      }
      return requests.getWithParams<string[]>("api/sks/fl/kampagne-names", {
        params: urlSearchParams,
      });
    },
    search: (body: SksFLSearchRequest) =>
      requests.post<SksFLSearchResponseView>("api/sks/fl/search", body),
    getSksFLHovedkontoReferencesByPeriod: (period: number, paragrafNumbers: string[]) => {
      const urlSearchParams = new URLSearchParams();
      urlSearchParams.set("period", period.toString());
      if (paragrafNumbers.length > 0) {
        paragrafNumbers.forEach((element) => {
          urlSearchParams.append("paragrafNumre", element);
        });
      }
      return requests.getWithParams<SksFLHovedkontoReferenceView[]>(
        "api/sks/fl/hovedkonto/references",
        {
          params: urlSearchParams,
        }
      );
    },
  },
  Reg: {
    getPeriods: () => requests.get<number[]>("api/sks/reg/period"),
    search: (body: SksRegSearchRequest) =>
      requests.post<SksRegSearchResponseView>("api/sks/reg/search", body),
    getDetail: (period?: number, nummer?: string, outputLevel?: SksRegOutputLevel) => {
      const urlSearchParams = new URLSearchParams();
      if (period !== undefined) {
        urlSearchParams.set("period", period.toString());
      }
      if (nummer) {
        urlSearchParams.set("nummer", nummer);
      }
      if (outputLevel) {
        urlSearchParams.set("outputLevel", outputLevel);
      }
      return requests.getWithParams<SksRegDetailHeaderView>("api/sks/reg/detail", {
        params: urlSearchParams,
      });
    },
  },
};

const ApiService = {
  FLStructureElements,
  PutFLStructure,
  DeleteFLElement,
  DeleteACElement,
  SoftCloseFLElement,
  AccountingStructureElements,
  Processes: Processes,
  PeriodElements,
  Aktiviteter,
  PutACElement,
  UserRoles,
  CreateACElement,
  CreateFLElement,
  FinancialYears,
  ACAndFLRelation,
  SLP,
  Datafordeler,
  Kontobroer,
  Report,
};

export default ApiService;
